<template>
  <div class="chat-page">
    <aside class="chat-sidebar" :class="{ open: sidebarOpen }">
      <div class="chat-sidebar-head">
        <a class="chat-brand" href="/">chatgpt</a>
        <button class="icon-btn mobile-only" type="button" aria-label="关闭侧栏" @click="sidebarOpen = false"><i class="el-icon-close"></i></button>
      </div>
      <button class="new-session-btn" type="button" @click="newSession"><i class="el-icon-plus"></i><span>新建会话</span></button>
      <div class="session-list">
        <button v-for="session in sessions" :key="session.id" type="button" :class="['session-item', { active: session.id === activeSessionId }]" @pointerdown.prevent="selectSession(session.id)" @click.prevent="selectSession(session.id)">
          <i class="el-icon-chat-line-round"></i><span>{{ session.title || '新会话' }}</span>
          <i class="el-icon-delete session-delete" title="删除会话" @click.stop="deleteSession(session)"></i>
        </button>
        <p v-if="!sessions.length" class="session-empty">还没有历史会话</p>
      </div>
      <div class="chat-sidebar-foot">
        <a href="/dashboard"><i class="el-icon-s-grid"></i>返回控制台</a>
      </div>
    </aside>
    <div v-if="sidebarOpen" class="sidebar-overlay" @click="sidebarOpen = false"></div>

    <main class="chat-main">
      <header class="chat-header">
        <button class="icon-btn mobile-only" type="button" aria-label="打开侧栏" @click="sidebarOpen = true"><i class="el-icon-menu"></i></button>
        <div class="chat-header-title"><strong>{{ activeSession ? activeSession.title : '新会话' }}</strong></div>
        <div class="chat-header-controls">
          <el-select v-model="selectedKeyId" size="small" placeholder="选择 API Key" @change="handleKeyChange">
            <el-option v-for="key in keys" :key="key.id" :label="key.keyName || maskKey(key.apiKey)" :value="key.id" />
          </el-select>
          <el-select v-model="model" size="small" :loading="loadingModels" :disabled="!selectedKey || loadingModels || !models.length" placeholder="选择模型" @change="persistModel">
            <el-option v-for="item in models" :key="item" :label="item" :value="item" />
          </el-select>
        </div>
      </header>

      <section ref="messages" class="messages" aria-live="polite">
        <div v-if="!activeMessages.length" class="welcome">
          <div class="welcome-mark"><i class="el-icon-chat-dot-round"></i></div>
          <h1>有什么可以帮你？</h1>
          <p>选择左上角 API Key，开始一段新的对话。</p>
        </div>
        <article v-for="(item, index) in activeMessages" :key="index" :class="['message-row', item.role]">
          <div class="message-time">{{ formatMessageTime(item) }}</div>
          <div v-if="item.role === 'user'" class="message-content user-content">{{ item.content }}</div>
          <div v-else class="message-content assistant-content" v-html="renderMarkdown(item.content)"></div>
          <span v-if="item.pending" class="typing-caret"></span>
        </article>
      </section>

      <footer class="composer-wrap">
        <div class="composer">
          <div class="composer-input">
            <textarea ref="composerTextarea" :value="draft" rows="1" :disabled="sending" placeholder="输入消息..." @input="handleInput" @keydown="handleKeydown"></textarea>
          </div>
          <div class="composer-actions">
            <span class="composer-state" aria-live="polite">{{ sending ? '正在生成' : '' }}</span>
            <div class="composer-action-buttons">
              <button v-if="sending" class="composer-button stop-button" type="button" title="停止生成" @click="stop"><i class="el-icon-video-pause"></i><span>停止</span></button>
              <button v-else class="composer-button send-button" type="button" title="发送" :disabled="!canSend" @click="send"><i class="el-icon-position"></i><span>发送</span></button>
            </div>
          </div>
        </div>
      </footer>
    </main>
  </div>
</template>

<script>
import { fetchApiKeys } from '../api/keys';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const TOKEN_KEY = 'codex-api-auth-token';
const KEY_PREF = 'codex-chat-selected-key';
const MODEL_PREF_PREFIX = 'codex-chat-selected-model:';

export default {
  name: 'ChatPage',
  data() { return { keys: [], selectedKeyId: null, models: [], loadingModels: false, model: '', sessions: [], activeSessionId: null, messagesBySession: {}, loadingSessions: {}, draft: '', sending: false, abortController: null, sidebarOpen: false }; },
  computed: {
    activeSession() { return this.sessions.find(item => item.id === this.activeSessionId); },
    activeMessages() { return this.messagesBySession[this.activeSessionId] || []; },
    selectedKey() { return this.keys.find(item => item.id === this.selectedKeyId); },
    canSend() { return Boolean(this.selectedKey && this.model && this.draft.trim() && this.activeSessionId); }
  },
  created() { document.body.classList.add('chat-page-body'); this.init(); },
  beforeDestroy() { document.body.classList.remove('chat-page-body'); this.stop(); },
  methods: {
    async init() {
      try {
        const keyResponse = await fetchApiKeys();
        this.keys = keyResponse.data && keyResponse.data.code === 0 ? (keyResponse.data.data || []).filter(item => item.status === 1) : [];
        const preferred = Number(window.localStorage.getItem(KEY_PREF));
        this.selectedKeyId = this.keys.some(item => item.id === preferred) ? preferred : (this.keys[0] && this.keys[0].id);
        await this.loadModels();
        await this.loadSessions();
      } catch (error) { this.$message.error(error.message || '聊天初始化失败'); }
    },
    async request(path, options) {
      const token = window.localStorage.getItem(TOKEN_KEY);
      const response = await window.fetch('/api/chat' + path, Object.assign({ headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token } }, options || {}));
      const payload = await response.json();
      if (!response.ok || payload.code !== 0) throw new Error(payload.message || (payload.error && payload.error.message) || '请求失败');
      return payload.data;
    },
    async loadSessions() {
      this.sessions = await this.request('/sessions');
      if (!this.sessions.length) { await this.newSession(); return; }
      await Promise.all(this.sessions.map(async session => {
        try { this.$set(this.messagesBySession, session.id, await this.request('/sessions/' + session.id + '/messages')); }
        catch (error) { this.$set(this.messagesBySession, session.id, []); }
      }));
      this.selectSession(this.sessions[0].id);
    },
    async newSession() {
      const session = await this.request('/sessions', { method: 'POST', body: JSON.stringify({ title: '新会话', model: this.model }) });
      this.sessions.unshift(session); this.$set(this.messagesBySession, session.id, []); this.activeSessionId = session.id; this.sidebarOpen = false;
    },
    selectSession(id) {
      this.activeSessionId = id;
      this.sidebarOpen = false;
      if (!this.messagesBySession[id]) this.$set(this.messagesBySession, id, []);
      this.scrollBottom();
    },
    async deleteSession(session) { try { await this.request('/sessions/' + session.id, { method: 'DELETE' }); this.sessions = this.sessions.filter(item => item.id !== session.id); this.$delete(this.messagesBySession, session.id); if (this.activeSessionId === session.id) { this.activeSessionId = null; await this.newSession(); } } catch (error) { this.$message.error(error.message); } },
    persistKey() { window.localStorage.setItem(KEY_PREF, String(this.selectedKeyId)); },
    persistModel() {
      if (this.selectedKeyId && this.model) window.localStorage.setItem(MODEL_PREF_PREFIX + this.selectedKeyId, this.model);
    },
    async handleKeyChange() {
      this.persistKey();
      try { await this.loadModels(); }
      catch (error) { this.$message.error(error.message || '模型列表加载失败'); }
    },
    async loadModels() {
      const key = this.selectedKey;
      this.models = [];
      this.model = '';
      if (!key || !key.apiKey) return;
      this.loadingModels = true;
      try {
        const response = await window.fetch('/v1/models', { headers: { Authorization: 'Bearer ' + key.apiKey } });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error((payload && payload.error && payload.error.message) || '模型列表请求失败');
        const models = payload && Array.isArray(payload.data) ? payload.data
          .map(item => typeof item === 'string' ? item : item && item.id)
          .filter(Boolean) : [];
        if (key.id !== this.selectedKeyId) return;
        this.models = Array.from(new Set(models));
        const preferred = window.localStorage.getItem(MODEL_PREF_PREFIX + key.id);
        this.model = this.models.includes(preferred) ? preferred : (this.models[0] || '');
        if (this.model) this.persistModel();
      } finally {
        if (key.id === this.selectedKeyId) this.loadingModels = false;
      }
    },
    maskKey(value) { return value ? value.slice(0, 5) + '...' + value.slice(-4) : 'API Key'; },
    formatSessionTitle(value) {
      const chars = Array.from(String(value || '').trim().replace(/\s+/g, ' '));
      return chars.length > 10 ? chars.slice(0, 10).join('') + '...' : chars.join('');
    },
    formatMessageTime(item) {
      const value = item && (item.createdAt || item.created_at || item.createTime || item.timestamp);
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const pad = number => String(number).padStart(2, '0');
      return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
        + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes());
    },
    renderMarkdown(value) {
      if (!value) return '';
      const html = marked.parse(String(value), { breaks: true, gfm: true, headerIds: false, mangle: false });
      return DOMPurify.sanitize(html, { ADD_ATTR: ['target', 'rel'] });
    },
    handleInput(event) {
      this.draft = event.target.value;
      event.target.style.height = 'auto';
      event.target.style.height = Math.min(event.target.scrollHeight, 160) + 'px';
    },
    handleKeydown(event) { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); this.send(); } },
    async send() {
      if (!this.canSend || this.sending) return;
      const text = this.draft.trim(); this.draft = '';
      this.$nextTick(() => { if (this.$refs.composerTextarea) this.$refs.composerTextarea.style.height = 'auto'; });
      const list = this.messagesBySession[this.activeSessionId]; const messageTime = new Date().toISOString(); list.push({ role: 'user', content: text, createTime: messageTime }); const assistant = { role: 'assistant', content: '', targetContent: '', streamDone: false, _typingTimer: null, createTime: messageTime, pending: true }; list.push(assistant); this.sending = true; this.abortController = new AbortController(); this.scrollBottom();
      try {
        const input = list.slice(0, -1).map(item => ({ role: item.role, content: [{ type: item.role === 'assistant' ? 'output_text' : 'input_text', text: item.content }] }));
        const isFirstQuestion = list.filter(item => item.role === 'user').length === 1;
        const title = isFirstQuestion ? this.formatSessionTitle(text) : '';
        if (isFirstQuestion && title && this.activeSession) this.activeSession.title = title;
        const response = await window.fetch('/api/chat/responses', { method: 'POST', signal: this.abortController.signal, headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + window.localStorage.getItem(TOKEN_KEY), Accept: 'text/event-stream' }, body: JSON.stringify({ sessionId: this.activeSessionId, apiKeyId: this.selectedKeyId, model: this.model, message: text, input, title }) });
        if (!response.ok) {
          let message = '请求失败（HTTP ' + response.status + '）';
          try {
            const payload = await response.json();
            message = (payload.error && payload.error.message) || payload.message || message;
          } catch (error) {
            // Keep the HTTP status when the upstream response is not JSON.
          }
          throw new Error(message);
        }
        const reader = response.body.getReader(); const decoder = new TextDecoder(); let buffer = '';
        let finished = false;
        while (!finished) {
          const part = await reader.read();
          finished = part.done;
          buffer += decoder.decode(part.value || new Uint8Array(), { stream: !part.done });
          const events = buffer.split(/\r?\n\r?\n/);
          buffer = events.pop();
          events.forEach(event => event.split(/\r?\n/).filter(line => line.indexOf('data:') === 0).forEach(line => {
            const data = line.slice(5).trim();
            if (!data || data === '[DONE]') return;
            try {
              const payload = JSON.parse(data);
              const text = this.readResponseText(payload, !assistant.content);
              if (text) {
                this.queueAssistantText(assistant, text);
                assistant.createTime = new Date().toISOString();
                assistant.pending = false;
                this.scrollBottom();
              }
              if (payload.type === 'error' || payload.error) throw new Error((payload.error && payload.error.message) || payload.message || '模型返回错误');
            } catch (e) {
              if (e.message && e.message !== 'Unexpected end of JSON input') throw e;
              // Ignore incomplete SSE JSON chunks.
            }
          }));
        }
        assistant.pending = false; if (!assistant.content) assistant.content = '模型未返回文本内容。';
      } catch (error) { if (error.name === 'AbortError') { if (!assistant.content) list.pop(); } else { assistant.pending = false; assistant.content = '请求失败：' + error.message; } } finally { this.sending = false; this.abortController = null; this.scrollBottom(); }
    },
    queueAssistantText(assistant, text) {
      assistant.targetContent = (assistant.targetContent || '') + String(text);
      assistant.createTime = new Date().toISOString();
      if (assistant._typingTimer) return;
      const renderNext = () => {
        const displayed = Array.from(assistant.content || '');
        const target = Array.from(assistant.targetContent || '');
        if (displayed.length >= target.length) {
          assistant._typingTimer = null;
          return;
        }
        assistant.content = target.slice(0, displayed.length + 2).join('');
        assistant.pending = false;
        this.scrollBottom();
        assistant._typingTimer = window.setTimeout(renderNext, 16);
      };
      renderNext();
    },
    readResponseText(payload, allowCompletedFallback) {
      if (payload && payload.delta) return String(payload.delta);
      const choice = payload && Array.isArray(payload.choices) ? payload.choices[0] : null;
      if (choice) return (choice.delta && choice.delta.content) || (choice.message && choice.message.content) || '';
      if (!allowCompletedFallback || !payload || payload.type !== 'response.completed') return '';
      const output = payload.response && Array.isArray(payload.response.output) ? payload.response.output : [];
      return output.reduce((text, item) => text + ((item.content || []).filter(part => part.type === 'output_text').map(part => part.text || '').join('')), '');
    },
    stop() { if (this.abortController) this.abortController.abort(); },
    scrollBottom() { this.$nextTick(() => { const el = this.$refs.messages; if (el) el.scrollTop = el.scrollHeight; }); },
  }
};
</script>

<style scoped>
*{box-sizing:border-box}.chat-page{display:flex;width:100%;height:100vh;height:100dvh;overflow:hidden;background:#fff;color:#1f1f1f;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif}.chat-sidebar{display:flex;flex:0 0 272px;flex-direction:column;width:272px;height:100%;padding:20px 14px 14px;background:#fafafa;border-right:1px solid #e7e7e7;z-index:20}.chat-sidebar-head{display:flex;align-items:center;justify-content:space-between;min-height:32px;margin:0 8px 18px}.chat-brand{color:#171717;font-size:17px;font-weight:700;text-decoration:none}.new-session-btn,.session-item,.chat-sidebar-foot a,.chat-sidebar-foot button{display:flex;align-items:center;border:0;background:transparent;color:#444;font:inherit;text-align:left;cursor:pointer}.new-session-btn{justify-content:center;width:100%;height:40px;margin-bottom:14px;border:1px solid #dedede;border-radius:6px;background:#fff;color:#292929;font-size:13px;font-weight:600;gap:7px}.new-session-btn:hover{background:#f2f2f2;border-color:#c8c8c8}.session-list{flex:1;min-height:0;overflow-y:auto;padding:2px 0}.session-item{position:relative;width:100%;min-height:42px;padding:8px 10px;border-radius:5px;gap:9px;color:#454545;font-size:13px}.session-item>span{overflow:hidden;flex:1;text-overflow:ellipsis;white-space:nowrap}.session-item:hover{background:#f0f0f0}.session-item.active{background:#e7e7e7;color:#181818;font-weight:600}.session-delete{display:none;flex:0 0 auto;color:#8e8e8e;font-size:14px}.session-item:hover .session-delete,.session-item.active .session-delete{display:block}.session-delete:hover{color:#333}.session-empty{padding:22px 10px;color:#999;font-size:12px;text-align:center}.chat-sidebar-foot{display:flex;flex-direction:column;gap:3px;padding-top:12px;border-top:1px solid #e8e8e8}.chat-sidebar-foot a,.chat-sidebar-foot button{width:100%;padding:9px 10px;border-radius:5px;gap:9px;color:#777;font-size:12px;text-decoration:none}.chat-sidebar-foot a:hover,.chat-sidebar-foot button:hover{background:#ededed;color:#222}.chat-main{display:flex;flex:1;min-width:0;flex-direction:column;height:100%}.chat-header{display:flex;align-items:center;min-height:68px;padding:12px 28px;border-bottom:1px solid #e9e9e9;background:#fff;gap:14px}.chat-header-title{display:flex;min-width:0;flex:1;align-items:center;gap:10px}.chat-header-title strong{overflow:hidden;color:#222;font-size:15px;text-overflow:ellipsis;white-space:nowrap}.chat-header-title span{color:#999;font-size:11px;white-space:nowrap}.chat-header-controls{display:flex;align-items:center;justify-content:flex-end;gap:10px}.chat-header-controls .el-select{width:210px}.chat-model-label{display:inline-flex;align-items:center;min-height:32px;padding:0 10px;color:#666;background:#f5f5f5;border:1px solid #e2e2e2;border-radius:6px;font-size:12px;white-space:nowrap}.messages{flex:1;min-height:0;overflow-y:auto;padding:34px clamp(18px,7vw,110px) 26px}.welcome{display:flex;align-items:center;justify-content:center;height:100%;min-height:220px;flex-direction:column;color:#999;text-align:center}.welcome-mark{display:flex;align-items:center;justify-content:center;width:52px;height:52px;margin-bottom:14px;border:1px solid #dedede;border-radius:50%;background:#f7f7f7;color:#333;font-size:23px}.welcome h1{margin:0 0 8px;color:#111;font-size:20px;font-weight:600}.welcome p{margin:0;font-size:13px}.message-row{display:flex;max-width:900px;margin:0 auto 25px;align-items:flex-start;gap:12px}.message-row.user{flex-direction:row-reverse}.avatar{display:flex;flex:0 0 32px;align-items:center;justify-content:center;width:32px;height:32px;border-radius:50%;background:#ededed;color:#222;font-size:11px;font-weight:700}.message-row.user .avatar{background:#e9e9e9;color:#333}.message-body{min-width:0;max-width:calc(100% - 44px)}.message-row.user .message-body{text-align:right}.message-name{margin-bottom:5px;color:#999;font-size:11px}.message-content{display:inline-block;max-width:100%;padding:11px 14px;border:1px solid #e4e4e4;border-radius:8px;background:#fff;color:#292929;font-size:14px;line-height:1.65;text-align:left;white-space:pre-wrap;overflow-wrap:anywhere;box-shadow:0 1px 2px rgba(0,0,0,.03)}.message-row.user .message-content{border-color:#e3e3e3;background:#f5f5f5;color:#222}.typing-caret{display:inline-block;width:6px;height:16px;margin-left:3px;vertical-align:-3px;background:#444;animation:blink 1s step-end infinite}@keyframes blink{50%{opacity:0}}.composer-wrap{padding:13px clamp(18px,7vw,110px) 16px;border-top:1px solid #e9e9e9;background:#fff}.composer{display:flex;align-items:flex-end;max-width:900px;margin:0 auto;padding:7px 8px 7px 12px;border:1px solid #dedede;border-radius:18px;background:#fff;box-shadow:0 3px 12px rgba(0,0,0,.04)}.composer:focus-within{border-color:#999;box-shadow:0 3px 12px rgba(0,0,0,.06)}.composer .el-textarea{flex:1}.composer .el-textarea__inner{min-height:28px!important;padding:5px 4px;border:0;background:transparent;box-shadow:none;color:#222;font-size:14px;line-height:1.5;resize:none}.send-btn{display:flex;flex:0 0 34px;align-items:center;justify-content:center;width:34px;height:34px;margin-left:7px;border:0;border-radius:50%;background:#222;color:#fff;cursor:pointer}.send-btn:hover{background:#000}.send-btn:disabled{background:#ededed;color:#aaa;cursor:not-allowed}.send-btn.stop{background:#555}.send-btn.stop:hover{background:#222}.composer-note{max-width:900px;margin:7px auto 0;color:#999;font-size:11px;text-align:center}.icon-btn{display:flex;align-items:center;justify-content:center;width:34px;height:34px;border:1px solid #ddd;border-radius:6px;background:#fff;color:#333;cursor:pointer}.icon-btn:hover{background:#f1f1f1}.mobile-only{display:none}.sidebar-overlay{display:none}
@media (max-width:768px){.chat-sidebar{position:fixed;top:0;bottom:0;left:0;width:min(82vw,300px);transform:translateX(-102%);transition:transform .2s ease;box-shadow:5px 0 18px rgba(0,0,0,.12)}.chat-sidebar.open{transform:translateX(0)}.sidebar-overlay{display:block;position:fixed;inset:0;background:rgba(0,0,0,.25);z-index:10}.mobile-only{display:flex}.chat-header{min-height:60px;padding:10px 14px;gap:9px}.chat-header-title{gap:7px}.chat-header-title span{display:none}.chat-header-controls{max-width:57%;flex-wrap:wrap;gap:6px}.chat-header-controls .el-select{width:min(48vw,190px)}.chat-model-label{max-width:100%;overflow:hidden;text-overflow:ellipsis}.messages{padding:24px 14px 18px}.message-row{gap:8px;margin-bottom:20px}.message-body{max-width:calc(100% - 40px)}.message-content{padding:9px 11px;font-size:13px}.composer-wrap{padding:10px 12px 12px}.composer{border-radius:26px;padding:10px 10px 10px 16px}.send-btn{width:40px;height:40px;flex-basis:40px}.composer-note{font-size:10px}.welcome-mark{display:none}.welcome h1{font-size:clamp(30px,8.5vw,42px);line-height:1.2}.welcome p{padding:0 20px;line-height:1.6}}
@media (max-width:420px){.chat-header{align-items:flex-start;flex-wrap:wrap}.chat-header-title{padding-top:7px}.chat-header-controls{width:100%;max-width:none;padding-left:43px}.chat-header-controls .el-select{width:calc(100% - 115px);min-width:0}.chat-model-label{flex:1;min-width:0}.messages{padding-top:18px}.welcome{justify-content:center;min-height:360px}.welcome h1{margin-bottom:20px;white-space:nowrap}.welcome p{display:none}.avatar{flex-basis:28px;width:28px;height:28px;font-size:10px}.message-content{font-size:13px}.composer .el-textarea__inner{font-size:13px}}
</style>

<style>
.chat-page-body {
  min-width: 0 !important;
  overflow: hidden !important;
  background: #fff !important;
  color: #1f1f1f !important;
}
.chat-page .composer .el-textarea,
.chat-page .composer .el-textarea__inner {
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  outline: 0 !important;
  background: transparent !important;
}
.chat-page .chat-main,
.chat-page .messages,
.chat-page .composer-wrap,
.chat-page .composer,
.chat-page .composer-input,
.chat-page .composer-input .el-textarea,
.chat-page .composer-input .el-input {
  min-width: 0;
  max-width: 100%;
}
.chat-page .composer-input .el-textarea__inner {
  width: 100% !important;
  max-width: 100% !important;
  overflow-x: hidden !important;
}
.chat-page .composer-input textarea {
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: 28px;
  max-height: 160px;
  padding: 5px 4px;
  border: 0;
  outline: 0;
  background: transparent;
  color: #222;
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  overflow-x: hidden;
  overflow-y: auto;
}
.chat-page .composer-input textarea::placeholder { color: #aaa; }
.chat-page .composer-input textarea:disabled { cursor: wait; opacity: .7; }
.chat-page .composer {
  flex-direction: column;
  align-items: stretch;
  padding: 10px 12px 8px;
}
.chat-page .composer-input {
  width: 100%;
}
.chat-page .composer-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 38px;
  padding-top: 5px;
}
.chat-page .composer-state {
  color: #999;
  font-size: 11px;
}
.chat-page .composer-action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}
.chat-page .composer-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 68px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  background: #fff;
  color: #333;
  font-size: 12px;
  cursor: pointer;
  gap: 5px;
}
.chat-page .composer-button:hover { background: #f3f3f3; border-color: #bdbdbd; }
.chat-page .composer-button:disabled { background: #f1f1f1; color: #aaa; cursor: not-allowed; }
.chat-page .composer-button.send-button { border-color: #222; background: #222; color: #fff; }
.chat-page .composer-button.send-button:hover { background: #000; border-color: #000; }
.chat-page .composer-button.send-button:disabled { border-color: #e1e1e1; background: #e1e1e1; color: #aaa; }
.chat-page .composer-button.stop-button { border-color: #bdbdbd; color: #333; }
@media (max-width:768px) {
  .chat-page .composer { padding: 10px 14px 9px; }
  .chat-page .composer-actions { min-height: 42px; }
  .chat-page .composer-button { min-width: 72px; height: 36px; border-radius: 7px; }
  .chat-page .composer-input textarea { min-height: 44px; max-height: 32vh; padding: 10px 4px 5px; font-size: 16px; }
}
.chat-page .chat-header { flex-wrap: nowrap; }
.chat-page .chat-header-controls { flex: 0 0 auto; max-width: none; flex-wrap: nowrap; }
.chat-page .chat-header-controls .el-select { width: 155px; }
@media (max-width:768px) {
  .chat-page .chat-header-controls { width: auto; padding-left: 0; }
  .chat-page .chat-header-controls .el-select { width: 132px; }
}
@media (max-width:420px) {
  .chat-page .chat-header { align-items: center; }
  .chat-page .chat-header-title { padding-top: 0; }
  .chat-page .chat-header-controls .el-select { width: 118px; }
}
@media (max-width:768px) {
  .chat-page { position: fixed; inset: 0; width: 100%; height: auto; min-height: 100%; }
}
.chat-page .message-row {
  display: block;
  max-width: 900px;
  margin: 0 auto 28px;
}
.chat-page .message-row.user {
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}
.chat-page .message-body,
.chat-page .avatar,
.chat-page .message-name { display: none; }
.chat-page .message-time {
  margin: 0 0 2px;
  color: #a0a0a0;
  font-size: 11px;
  line-height: 1.4;
}
.chat-page .message-row.user .message-time { text-align: right; }
.chat-page .message-content {
  display: block;
  max-width: 100%;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #27364d;
  font-size: 16px;
  line-height: 1.75;
  box-shadow: none;
  text-align: left;
}
.chat-page .message-content.user-content {
  display: inline-block;
  padding: 12px 15px;
  border: 1px solid #e1e5ea;
  border-radius: 9px;
  background: #f2f4f7;
  color: #36445a;
  white-space: pre-wrap;
}
.chat-page .message-row.user { text-align: right; }
.chat-page .message-row.user .user-content { text-align: left; }
.chat-page .assistant-content { white-space: normal; }
.chat-page .assistant-content > :first-child { margin-top: 0; }
.chat-page .assistant-content > :last-child { margin-bottom: 0; }
.chat-page .assistant-content p { margin: 0 0 8px; }
.chat-page .assistant-content h1,
.chat-page .assistant-content h2,
.chat-page .assistant-content h3,
.chat-page .assistant-content h4 { margin: 14px 0 6px; color: #1f2f47; line-height: 1.35; }
.chat-page .assistant-content h1 { font-size: 24px; }
.chat-page .assistant-content h2 { font-size: 21px; }
.chat-page .assistant-content h3 { font-size: 18px; }
.chat-page .assistant-content ul,
.chat-page .assistant-content ol { margin: 0 0 16px; padding-left: 25px; }
.chat-page .assistant-content li { margin: 5px 0; }
.chat-page .assistant-content blockquote { margin: 8px 0; padding: 4px 14px; border-left: 3px solid #cbd3df; color: #68758a; }
.chat-page .assistant-content code { padding: 2px 5px; border: 1px solid #dce1e8; border-radius: 4px; background: #f1f3f6; color: #34445b; font: 13px/1.5 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace; }
.chat-page .assistant-content pre { margin: 8px 0; padding: 13px 15px; overflow-x: auto; border: 1px solid #e0e4e9; border-radius: 7px; background: #f6f7f8; white-space: pre; }
.chat-page .assistant-content pre code { padding: 0; border: 0; background: transparent; }
.chat-page .assistant-content a { color: #2864b0; text-decoration: underline; }
.chat-page .assistant-content table { width: 100%; margin: 8px 0; border-collapse: collapse; font-size: 14px; }
.chat-page .assistant-content th,
.chat-page .assistant-content td { padding: 8px 10px; border: 1px solid #e0e4e9; text-align: left; }
.chat-page .assistant-content th { background: #f3f4f6; font-weight: 600; }
@media (max-width:768px) {
  .chat-page .message-row { margin-bottom: 24px; }
  .chat-page .message-content { font-size: 15px; line-height: 1.7; }
  .chat-page .message-content.user-content { padding: 10px 12px; }
  .chat-page .assistant-content h1 { font-size: 22px; }
  .chat-page .assistant-content h2 { font-size: 19px; }
  .chat-page .assistant-content h3 { font-size: 17px; }
}

/* Keep the conversation compact on small screens. */
.chat-page .messages { padding-top: 18px; padding-bottom: 12px; }
.chat-page .message-row { margin-bottom: 4px; }
.chat-page .assistant-content p { margin-bottom: 2px; }
.chat-page .assistant-content ul,
.chat-page .assistant-content ol { margin-bottom: 3px; }
.chat-page .assistant-content li { margin: 0; }
.chat-page .assistant-content li > p { margin: 0; }
.chat-page .assistant-content ul,
.chat-page .assistant-content ol { padding-left: 22px; }
@media (max-width:768px) {
  .chat-page .messages { padding-top: 12px; padding-bottom: 8px; }
  .chat-page .message-row { margin-bottom: 6px; }
  .chat-page .message-content { line-height: 1.55; }
}
</style>
